import React, { useEffect, useState, useRef } from 'react';
import { Tab } from "./Tab";
import styles from "./Plan.module.scss";
import global from '../../../styles/index.module.scss';
import { fetchAnswerEstimates } from '../../../api/plan';
import { ApiResponse } from '../../../types/planModel';
import IconBottomArrowWhite from "../../../assets/img/common/icon_bottom_arrow_white.svg";
import { sanitizeHtmlAllowingLinks } from '../../../utils/sanitizeHtml';

// 画像読み込み
import imgBird from "../../../assets/img/plan/img_bird.png";

export const Plan: React.FC = () => {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState('0px');
  const contentRef = useRef(null);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? `${(contentRef.current as HTMLElement).scrollHeight}px` : '0px');
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 現在のURLからパラメーターを取得
        const urlParams = new URLSearchParams(window.location.search);
        const uuid = urlParams.get('uuid');

        if (uuid) {
          const apiData = await fetchAnswerEstimates(uuid);
          setData(apiData);
        } else {
          console.error('UUIDパラメーターが見つかりません');
        }

      } catch (error) {
        console.error('データの取得に失敗しました', error);
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // const data = mockApiResponse
  const resultId = data && data.body.result.id;
  const resultMessage = data && data.body.result.message;
  const answerEstimatesData = data && data.body.answer_estimates;

  const contactUrl: string | undefined = answerEstimatesData && answerEstimatesData.length > 0 ? answerEstimatesData[0].contact_url : undefined;

  if (loading) {
    return <p>読み込み中...</p>;
  }

  if (error) {
    return <p>{error}</p>;
  }

  // URLからsrcパラメータを取得
  const urlParams = new URLSearchParams(window.location.search);
  const src = urlParams.get('src');

  return (
    <div className={styles.planWrapper}>
      <div className={styles.pcContainer}>
        <h1 className={styles.headingLv1}>
          ご相談いただいた修理の
          <br />
          回答・お見積りがご用意できました。
        </h1>
        {
          resultId === 0 && answerEstimatesData && answerEstimatesData.map((estimate, index) => (
            <p key={index} className={styles.termText}>
              {estimate.effective_date}
            </p>
          ))
        }
        <div className={styles.jumbotronImg}>
          <img src={imgBird} alt="鳥の画像" width={145} height={98} />
        </div>
        {
          resultId !== 0 && src !== 'mgmt' &&
          <div className={styles.requestMessageWrapper}>
            <p className={styles.requestMessage}>{resultMessage}</p>
          </div>
        }
        <section id="operator-response" className={styles.operatorResponse}>
          {answerEstimatesData && (
            <div>
              <h2 className={styles.headingLv2}>修理スタッフによる回答・お見積り</h2>
              <dl className={styles.orderList}>
                {answerEstimatesData.map((estimate, index) => (
                  <React.Fragment key={index}>
                    <div className={styles.orderListItem}>
                      <dt className={styles.orderListLabel}>修理可否</dt>
                      <dd className={styles.orderListValue}>{estimate.repair_possibility}</dd>
                    </div>
                    {estimate.message && (
                      <div className={`${styles.orderListItem} ${styles.Column}`}>
                        <dt className={styles.orderListLabel}>お見積り内容についてご説明（修理スタッフより）</dt>
                        <dd className={styles.orderListValue}>
                          <div
                            dangerouslySetInnerHTML={{ __html: sanitizeHtmlAllowingLinks(estimate.message) }}
                          />
                        </dd>
                      </div>
                    )}
                    {
                      estimate.image_url.some(url => url) && (
                        <ul className={styles.messageImgList}>
                          {
                            estimate.image_url.map((imageUrl, index) => (
                              imageUrl && (
                                <li className={styles.messageImgItem} key={index}>
                                  <picture className={styles.messageImg}>
                                    <img
                                      src={imageUrl}
                                      alt="修理前画像"
                                      width={343}
                                      height={193}
                                    />
                                  </picture>
                                </li>
                              )
                            ))
                          }
                        </ul>
                      )
                    }
                  </React.Fragment>
                ))}
              </dl>
              {
                resultId === 3 && src !== 'mgmt' && (
                  <div className={styles.contactArea}>
                    <p className={styles.contactText}>こちらの返信は以下のボタンからお願いいたします。</p>
                    <div className={global.buttonWrapper}>
                      <a href={contactUrl} className={global.primaryButton}>返信する</a>
                    </div>
                  </div>
                )
              }
              { (resultId === 0 || src === 'mgmt') && (
                <>
                  <h2 className={styles.headingLv2}>修理ご相談内容</h2>
                  <dl className={styles.orderList}>
                    {answerEstimatesData.map((estimate, index) => (
                      <div key={index} className={styles.accordionItem}>
                        <div className={styles.accordionButton}>
                          <span className={styles.orderListLabel}>修理ご相談アイテム</span>
                          <span className={styles.orderListValue}>{estimate.order_details}</span>
                        </div>
                        <div
                          ref={contentRef}
                          style={{ height: height }}
                          className={`${styles.accordionContent} ${isOpen ? styles.open : ''}`}
                        >
                        <div className={styles.orderListItem}>
                          <dt className={styles.orderListLabel}>相談内容</dt>
                          <dd className={styles.orderListValue}>{estimate.item_name}</dd>
                        </div>
                        {estimate.item_img.some((imgSrc) => imgSrc.trim() !== "") && (
                          <div className={`${styles.orderListItem} ${styles.Column}`}>
                            <dt className={styles.orderListLabel}>商品写真</dt>
                            <dd className={styles.orderListValue}>
                              <div className={styles.orderImages}>
                                {estimate.item_img
                                  .filter((imgSrc) => imgSrc.trim() !== "") // 空文字を除外
                                  .map((imgSrc, index) => (
                                    <div key={index} className={styles.orderImage}>
                                      <img src={imgSrc} alt={`商品画像${index + 1}`} width={100} height={100} />
                                    </div>
                                  ))}
                              </div>
                            </dd>
                          </div>
                        )}
                        <div className={`${styles.orderListItem} ${styles.Column}`}>
                          <dt className={styles.orderListLabel}>お問い合わせ内容</dt>
                          <dd className={styles.orderListValue}>
                            <p className={styles.orderListLabel}>メールアドレス</p>
                            <p className={styles.orderListContent}>{estimate.email}</p>
                          </dd>
                          <dd className={styles.orderListValue}>
                            <p className={styles.orderListLabel}>本文</p>
                            <p className={styles.orderListContent}>
                              {estimate.request}
                            </p>
                          </dd>
                        </div>
                      </div>
                    </div>
                    ))}
                  </dl>
                  <button type='button' className={`${styles.accordionToggle} ${isOpen ? styles.isOpened : ''}`} onClick={toggleAccordion}>
                    <span className={styles.accordionToggleIcon}>
                      <IconBottomArrowWhite />
                    </span>
                  </button>
                </>
                )
              }
            </div>
          )}
        </section>
        { (resultId === 0 || src === 'mgmt') && (
          <section id="repair-and-estimate">
            <h2 className={styles.headingLv2}>修理プランのご提案</h2>
            {answerEstimatesData && (resultId || resultId === 0) ? <Tab activeTab="store" data={answerEstimatesData} contactUrl={contactUrl}/> : <p>データがありません。</p>}
          </section>
        )}
      </div>
    </div>
  );
};
